import React, { FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getPaymentMethods } from 'app/wallet/store/wallet.actions';
import {
  walletCardsPendingSelector,
  walletCardsSelector,
  walletCheckoutSessionPendingSelector,
  walletStripeWalletSelector,
} from 'app/wallet/store/wallet.selectors';
import { userSelector } from 'app/auth/store/auth.selectors';

import {
  CardAmericanExpressIcon,
  CardDiscoveryIcon,
  CardMasterCardIcon,
  CardVisaIcon,
  ApplePayIcon,
  GooglePayIcon,
} from 'assets/icons';
import { PaymentCardDto } from 'types/payment-card.type';
import { RoutesList } from 'enums/routes-list.enum';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AppBackdrop from './app-backdrop';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import { colors } from 'constants/colors';

const StyledCardVisaIcon = styled(CardVisaIcon)({
  width: '46px !important',
  height: '30px',
});
const StyledCardMasterCardIcon = styled(CardMasterCardIcon)({
  width: '46px !important',
  height: '30px',
});
const StyledCardAmericanExpressIcon = styled(CardAmericanExpressIcon)({
  width: '46px !important',
  height: '30px',
});
const StyledCardDiscoveryIcon = styled(CardDiscoveryIcon)({
  width: '46px !important',
  height: '30px',
});
const StyledApplePayIcon = styled(ApplePayIcon)({
  width: '46px !important',
  height: '30px',
});
const StyledGooglePayIcon = styled(GooglePayIcon)({
  width: '46px !important',
  height: '30px',
});
const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: colors.default.black,
    border: `1px solid ${colors.default.borderLightGray} `,
  },
  '& .MuiList-root': {
    width: '375px',
    maxHeight: '124px',
    borderRadius: '32px',
    [theme.breakpoints.down(400)]: {
      width: '350px',
    },
  },
}));

const StyledAddCardBtn = styled(Button)({
  width: '100%',
  borderRadius: 0,
  height: '50px',
});

const CardIconComponent: FC<{ type?: string }> = ({ type }) => {
  return (
    <>
      {type === 'visa' && <StyledCardVisaIcon />}
      {type === 'master-card' && <StyledCardMasterCardIcon />}
      {type === 'american-express' && <StyledCardAmericanExpressIcon />}
      {type === 'discovery' && <StyledCardDiscoveryIcon />}
      {type === 'apple' && <StyledApplePayIcon />}
      {type === 'google' && <StyledGooglePayIcon />}
    </>
  );
};

interface CardSelectorProps {
  selectedCard: PaymentCardDto | undefined;
  walletPaymentMethod: string | null;
  onCardSelected: (card: PaymentCardDto | string) => any;
}
const CardSelector: FC<CardSelectorProps> = ({
  selectedCard,
  walletPaymentMethod,
  onCardSelected,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  const user = useSelector(userSelector);
  const cards = useSelector(walletCardsSelector);
  const stripeWallet = useSelector(walletStripeWalletSelector);
  const pending_cards = useSelector(walletCardsPendingSelector);
  const pending_checkout = useSelector(walletCheckoutSessionPendingSelector);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openMenu = Boolean(anchorEl);
  const pending = pending_cards || pending_checkout;

  const walletMethodString = stripeWallet?.applePay
    ? t('wallet.apple-pay')
    : t('wallet.google-pay');

  const walletTypeString = stripeWallet?.applePay
    ? t('wallet.apple')
    : t('wallet.google');

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectCard = useCallback(
    (card: PaymentCardDto | string) => {
      onCardSelected(card);
      handleClose();
    },
    [onCardSelected]
  );

  const handleAddCard = () =>
    navigate(RoutesList.Wallet_AddCard, {
      state: {
        from: location.pathname,
      },
    });

  useEffect(() => {
    if (!user) {
      return;
    }

    dispatch<any>(getPaymentMethods(null));
  }, [user, dispatch]);

  return (
    <>
      {pending && <AppBackdrop />}
      {!pending && (selectedCard || walletPaymentMethod) && (
        <>
          <Button
            variant="card-button-dark"
            color="green"
            onClick={handleOpenMenu}
            startIcon={
              <CardIconComponent
                type={
                  walletPaymentMethod
                    ? walletPaymentMethod
                    : selectedCard?.cardBrand
                }
              />
            }
            endIcon={
              <IconButton
                className="button-outlined"
                color="green"
                sx={{
                  margin: '0 !important',
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            }
            sx={{ width: '100%' }}
          >
            <Typography
              variant="h6"
              color="white"
              sx={{ flex: '1 1', textAlign: 'left' }}
            >
              {walletPaymentMethod ? (
                <span className="title">{walletMethodString}</span>
              ) : (
                <>
                  <span className="title">
                    Card ending in&nbsp;
                    {selectedCard?.cardLast4}
                  </span>
                  <span className="subtitle">
                    expires&nbsp;
                    {selectedCard?.cardExpMonth}/{selectedCard?.cardExpYear}
                  </span>
                </>
              )}
            </Typography>
          </Button>
        </>
      )}

      {(walletPaymentMethod || selectedCard) && !pending && (
        <StyledMenu
          id="basic-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          sx={{ padding: 0, '& ul': { padding: 0 } }}
          PaperProps={{ className: 'styled-scrollbar' }}
        >
          {cards?.map((card) => (
            <MenuItem
              key={card.id}
              onClick={() => handleSelectCard(card)}
              sx={{ padding: 0 }}
            >
              <Button
                variant="card-button-dark"
                color="green"
                startIcon={<CardIconComponent type={card.cardBrand} />}
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{ flex: '1 1', textAlign: 'left' }}
                >
                  <span className="title">Card ending in {card.cardLast4}</span>
                  <span className="subtitle">
                    expires {card.cardExpMonth}/{card.cardExpYear}
                  </span>
                </Typography>
              </Button>
            </MenuItem>
          ))}
          {stripeWallet && (
            <MenuItem
              onClick={() => handleSelectCard(walletTypeString)}
              sx={{ padding: 0 }}
            >
              <Button
                variant="card-button-dark"
                color="green"
                startIcon={<CardIconComponent type={walletTypeString} />}
                sx={{ height: '88px' }}
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{ flex: '1 1', textAlign: 'left' }}
                >
                  <span className="title">{walletMethodString}</span>
                </Typography>
              </Button>
            </MenuItem>
          )}
          <MenuItem onClick={handleAddCard} sx={{ padding: 0 }}>
            <StyledAddCardBtn
              variant="contained"
              color="green"
              startIcon={<AddIcon />}
            >
              {t('buttons.add-new-card')}
            </StyledAddCardBtn>
          </MenuItem>
        </StyledMenu>
      )}

      {!pending && !cards && !walletPaymentMethod && (
        <Button
          variant="contained"
          color="green"
          onClick={handleAddCard}
          fullWidth
        >
          {t('buttons.add-payment-method')}
        </Button>
      )}
    </>
  );
};

export default CardSelector;
